import { initialize } from 'redux-form';
import history from 'router/history';
import {
  saveProfile,
  updateProfile,
  loadProfile
} from './profileActions';
import { ref, authRef } from '../services/firebaseRef.js';
import firebase from 'firebase';
import { publishEvent } from "services/events";

export const SET_TEMP_PASSWORD = 'SET_TEMP_PASSWORD';
export const CLEAR_TEMP_PASSWORD = 'CLEAR_TEMP_PASSWORD';

export const LOGIN = 'LOGIN';
export const LOGIN_ERROR = 'LOGIN_ERROR';

export const LOGOUT = 'LOGOUT';

export const REGISTER = 'REGISTER';
export const REGISTER_ERROR = 'REGISTER_ERROR';

export const PASSWORD_RESET_INIT = 'PASSWORD_RESET_INIT';
export const PASSWORD_RESET_SENT = 'PASSWORD_RESET_SENT';
export const PASSWORD_RESET_ERROR = 'PASSWORD_RESET_ERROR';

export const CHANGE_EMAIL = 'CHANGE_EMAIL';
export const CHANGE_EMAIL_ERROR = 'CHANGE_EMAIL_ERROR';

export const CHANGE_PASSWORD = 'CHANGE_PASSWORD';
export const CHANGE_PASSWORD_ERROR = 'CHANGE_PASSWORD_ERROR';

export function setTempPassword (tempPassword) {
  return {
    type: SET_TEMP_PASSWORD,
    tempPassword
  };
}

export function clearTempPassword () {
  return {
    type: CLEAR_TEMP_PASSWORD
  };
}

export function loginSucceeded (authData) {
  return {
    type: LOGIN,
    authData
  };
}

function loginFailed (error) {
  return {
    type: LOGIN_ERROR,
    error
  };
}

const AUTHENTICATED = "Authenticated";
export function loginWithAuthData ({ authData, password }) {
  const userId = authData.uid;
  return dispatch => {
    return (
      dispatch(loadProfile(userId))
      .then(profileData => {
        dataLayer.push({
            event: 'setUserId',
            userId
        });
        if (!profileData) {
          const error = new Error('profile is missing');
          error.code = 'profile/missing';
          throw error;
        }
        const eventData = {
          userId,
          email: authData.email, // GTM note: required for Intercom
          ...profileData
        };
        publishEvent(AUTHENTICATED, eventData);
        return dispatch(loginSucceeded(authData));
      })
      .then(() => {
        if (
          authData &&
          authData.password &&
          authData.password.isTemporaryPassword &&
          password
        ) {
          return dispatch(setTempPassword(password));
        }
      })
      .then(() => authData)
    );
  };
}

export function login (data) {
  const { email, password } = data;
  return dispatch => {
    return authRef.signInWithEmailAndPassword(email, password)
      .then(authData => {
        return dispatch(loginWithAuthData({ authData, password }));
      })
      .then(authData => {
        const {uid: userId} = authData;
        publishEvent('Signed In', { userId });
        return authData;
      })
      .catch(error => {
        dispatch(loginFailed(error));
        const errors = {};
        if (error) {
          switch (error.code) {
          case 'auth/invalid-email':
            errors.email = error.toString();
            break;
          case 'auth/user-disabled':
            errors.email = `Sorry, your account has been disabled. Please contact
            our support team on <a href="javascript:Intercom('showNewMessage')" target="_blank">support@planetprotectoracademy.com</a> and we'll do
            our best to help you get back on track!`;
            break;
          case 'auth/user-not-found':
            errors.email = `Sorry, we don't have that email address in our system.
            Please double check and try again! If you're having trouble logging
            in, please email us on <a href="javascript:Intercom('showNewMessage')" target="_blank">support@planetprotectoracademy.com</a> and we'd be
            happy to help!`;
            break;
          case 'auth/wrong-password':
            errors.password = `Oops, that doesn't seem to be the right password.
            Please try again or <a href="/forgotPassword">
            reset your password.</a>`;
            break;
          case 'profile/missing':
            errors.alert = `This user registration is incomplete, please use the registration form with the same email/password to complete the registration for this user.`
            break;
          case 'profile/deleted':
            errors.alert = `Your account has been disabled. Please <a href="javascript:Intercom('showNewMessage')" target="_blank">contact us</a> for further support.`;
            break;
          default:
            errors.alert = `There seems to be a problem getting you logged in.
            Please try again or contact our support team on
            <a href="javascript:Intercom('showNewMessage')" target="_blank">support@planetprotectoracademy.com</a> and we'll do our best to help you get
            back on track!`;
          }
        }
        throw errors;
      });
  };
}

export function resetLoginForm ({email = ''} = {}) {
  return dispatch => {
    dispatch(initialize('login', {email}));
  };
}

export function logout ({userId}) {
  publishEvent('Signed Out', { userId });
  authRef.signOut();
  setCookie('featuredLanguage', '', -1);
  setCookie('featuredPrograms', '', -1);
  setCookie('contractId', '', -1);
  setTimeout(() => history.replaceState(null, '/'), 0);
  return {
    type: LOGOUT
  };
}

export function registerSucceeded (userId) {
  return {
    type: REGISTER,
    userId
  };
}

function registerFailed (error) {
  return {
    type: REGISTER_ERROR,
    error
  };
}

export function register (data) {
  const { email, password } = data;
  let authData;
  return dispatch => {
    return authRef.createUserWithEmailAndPassword(email, password)
    .then(userData => {
      authData = userData;
      return dispatch(registerSucceeded(userData.uid));
    }, error => {
      if (error.code !== 'auth/email-already-in-use') {
        throw error;
      }
      return authRef.signInWithEmailAndPassword(email, password)
        .then(userData => {
          authData = userData;
          return ref.child(`users/${userData.uid}/profile`).once('value');
        })
        .then(profileRef => {
          const profile = profileRef && profileRef.val();
          if (profile) {
            const error = new Error('profile existing');
            error.code = 'profile/existing';
            throw error;
          }
        })
    }).then(() => {
      return dispatch(saveProfile(authData.uid, data));
    }).then(() => {
      return dispatch(loginWithAuthData({authData}));
    }).then(() => {
      //const userId = authData.uid;
      publishEvent("Registered", {
        userId,
        firstName: data.firstName,
        lastName: data.lastName,
        email: data.email,
        phone: data.phone,
        marketingOptIn: data.marketingOptIn,
        directPromotionOptIn: data.directPromotionOptIn,
        termsAccepted: data.termsAccepted,
        referrerUserId: data.referrerUserId
      });
      if (data.referrerUserId) {
        publishEvent('User Referral Completed', {
          userId: data.referrerUserId,
          refereeUserId: userId
        });
      }
      if (data.marketingOptIn) {
        const payload = {
          userId,
          email: data.email,
          listId: "marketing",
          consent: "explicit",
          location: "registration"
        };
        publishEvent('Subscribed', payload);
      }
      if (data.directPromotionOptIn) {
        const payload = {
          userId,
          email: data.email,
          listId: "directPromotion",
          consent: "explicit",
          location: "registration"
        };
        publishEvent('Subscribed', payload);
      }
      return userId;
    }).catch(error => {
      dispatch(registerFailed(error));
      const errors = {};
      if (error) {
        switch (error.code) {
        case 'auth/invalid-email':
          errors.email = error.toString();
          break;
        case 'auth/wrong-password':
        case 'profile/existing':
          errors.email = `Oops, someone has already registered with that email
          address. If it's yours and you're having trouble logging in, try
          resetting your password!`;
          break;
        case 'auth/weak-password':
          errors.alert = `Oops, it looks like your password is not strong enough.
          Please try again with a more secure password, thanks!`;
          break;
        default:
          errors.alert = `There seems to be a problem getting you registered on
          our system. Please try again or contact our support team on
          <a href="javascript:Intercom('showNewMessage')" target="_blank">support@planetprotectoracademy.com</a> and we'll do our best to get you
          started!`;
        }
      }
      throw errors;
    });
  };
}

export function completeRegistration (userId, profile, data) {
  return dispatch => {
    const newProfile = {...profile, ...data};
    return dispatch(updateProfile(userId, newProfile))
      .then(() => {
        publishEvent('Profile Completed', {
          userId,
          cityOrMunicipality: data.cityOrMunicipality,
          country: data.country,
          stateOrProvince: data.stateOrProvince,
          school: data.school
        });
      })
  }
}

export function resetRegisterEditProfileForm (values = {}) {
  return dispatch => {
    dispatch(initialize('registerEditProfile', values));
  };
}

export function resetForgotPasswordForm () {
  return dispatch => {
    dispatch({ type: PASSWORD_RESET_INIT });
    dispatch(initialize('forgotPassword', {}));
  };
}

export function sendPasswordReset (data) {
  return dispatch => {
    const { email, url } = data;
    return authRef.sendPasswordResetEmail(email, {url})
    .then(() => {
      dispatch({ type: PASSWORD_RESET_SENT });
    }).catch(error => {
      const errors = {};
      dispatch({
        type: PASSWORD_RESET_ERROR,
        error
      });
      if (error) {
        switch (error.code) {
        case 'auth/invalid-email':
        case 'auth/user-not-found':
          errors.email = error.toString();
          break;
        default:
          errors.alert = `Oops, we weren't able to send you the email to reset
          your password. Please try again or contact our support team on
          <a href="javascript:Intercom('showNewMessage')" target="_blank">support@planetprotectoracademy.com</a> and we'll get you back on track!`;
        }
      }
      throw errors;
    });
  };
}

export function emailChanged ({ oldEmail, newEmail }) {
  return {
    type: CHANGE_EMAIL,
    oldEmail,
    newEmail
  };
}

export function changeEmail ({ userId, oldEmail, newEmail, password }) {
  return dispatch => {
    const credential = firebase.auth.EmailAuthProvider.credential(oldEmail, password);
    return authRef.currentUser.reauthenticateWithCredential(credential)
    .then(() => {
      return authRef.currentUser.updateEmail(newEmail);
    }).then(() => dispatch(
      updateProfile(userId, {
        email: newEmail
      }, false)
    )).then(() => {
      publishEvent('Email Changed', {
        userId, oldEmail, newEmail
      });
      return dispatch(emailChanged({ oldEmail, newEmail }));
    }).catch(error => {
      const errors = {};
      dispatch({
        type: CHANGE_EMAIL_ERROR,
        error
      });
      if (error) {
        switch (error.code) {
        case 'auth/invalid-email':
          errors.email = error.toString();
          break;
        case 'auth/wrong-password':
          errors.password = error.toString();
          break;
        case 'auth/email-already-in-use':
          errors.email = error.toString();
          break;
        default:
          errors.alert = `Oops, there was a problem changing your email.
          Please try again later or contact our support team at
          <a href="javascript:Intercom('showNewMessage')" target="_blank">support@planetprotectoracademy.com</a> and we'll do our best to help you get
          back on track!`;
        }
      }
      throw errors;
    });
  };
}

export function resetChangeEmailForm () {
  return dispatch => {
    dispatch(initialize('changeEmail', {}));
  };
}

export function passwordChanged ({ email }) {
  return {
    type: CHANGE_PASSWORD,
    email
  };
}

export function changePassword ({ email, oldPassword, newPassword }) {
  return dispatch => {
    const credential = firebase.auth.EmailAuthProvider.credential(email, oldPassword);
    return authRef.currentUser.reauthenticate(credential)
    .then(() => {
      return authRef.currentUser.updatePassword(newPassword);
    }).then(() => {
      let userId = authRef.currentUser.uid;
      publishEvent('Password Changed', { userId });
      return dispatch(passwordChanged({ email }));
    }).then(() => {
      return dispatch(clearTempPassword());
    }).then(() => {
      return dispatch(login({ email, password: newPassword }));
    }).catch(error => {
      const errors = {};
      dispatch({
        type: CHANGE_PASSWORD_ERROR,
        error
      });
      if (error) {
        switch (error.code) {
        case 'auth/user-mismatch':
        case 'auth/user-not-found':
          errors.alert = `Oops, you are not authenticated properly.
          Please logout, login and try again.`;
          break;
        case 'auth/wrong-password':
          errors.oldPassword = error.toString();
          break;
        case 'auth/weak-password':
          errors.password = error.toString();
          break;
        default:
          errors.alert = `Oops, there was a problem changing your password.
          Please try again later or contact our support team at
          <a href="javascript:Intercom('showNewMessage')" target="_blank">support@planetprotectoracademy.com</a> and we'll do our best to help you get
          back on track!`;
        }
      }
      throw errors;
    });
  };
}

export function resetChangePasswordForm (tempPassword) {
  return dispatch => {
    dispatch(initialize('changePassword', {
      oldPassword: tempPassword
    }));
  };
}



// WEBPACK FOOTER //
// ./src/actions/authActions.js